/**
 * A list's state in the URL (TMM-B6, TMMRSL-0072): search, filters, page, tab and the selected
 * record, so Back and reload restore a list and a link reproduces it.
 *
 *  - Each key is declared with a default, a parser and a serializer (`stringParam`, `intParam`,
 *    `enumParam`). A malformed or unknown value reads as the default; nothing throws.
 *  - A default is omitted from the URL.
 *  - A change replaces the history entry; only an explicit navigation (opening a record:
 *    `set(…, { push: true })` or a link to `searchWith(…)`) adds one. That push remembers this
 *    entry as `state.panelFrom`. `dismiss(key)` goes back when that entry is the current list
 *    without `key`, and replaces otherwise (TMMRSL-0074).
 *  - The `page` key resets to its default, in the same write, when any other key of the hook
 *    changes; `clears` names params of a child list that a change here removes (People's tab).
 *    `set(patch, { forceClears: true })` removes them when the patch already matches the URL
 *    (a period pick of Current period). A plain `set` that changes nothing leaves them.
 *  - Params the hook doesn't own pass through untouched, and `location.state` is kept.
 *  - The `search` key is debounced: `searchText` is the text as typed, shown at once; the URL (and
 *    the load that reads it) follow 300 ms after the last keystroke, trimmed, with the page reset.
 *    A push cancels a pending write. `searchWith` flushes the draft into the link first.
 *
 * A schema is a module-level constant: the hook reads it on every render.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';

const SEARCH_DEBOUNCE_MS = 300;

export interface UrlParam<T> {
  readonly defaultValue: T;
  /** The value of a raw URL string, or `undefined` for one that isn't valid. */
  parse(raw: string): T | undefined;
  serialize(value: T): string;
}

export function stringParam(defaultValue = ''): UrlParam<string> {
  return { defaultValue, parse: (raw) => raw, serialize: (value) => value };
}

/** A whole number ≥ 0; `allowed` narrows it to a set (a page size). */
export function intParam(defaultValue = 0, allowed?: readonly number[]): UrlParam<number> {
  return {
    defaultValue,
    parse: (raw) => {
      if (!/^\d{1,15}$/.test(raw)) return undefined;
      const value = Number(raw);
      return allowed && !allowed.includes(value) ? undefined : value;
    },
    serialize: (value) => String(value),
  };
}

export function enumParam<const V extends string>(
  values: readonly V[],
  defaultValue: NoInfer<V>,
): UrlParam<V> {
  return {
    defaultValue,
    parse: (raw) => (values as readonly string[]).includes(raw) ? (raw as V) : undefined,
    serialize: (value) => value,
  };
}

type Schema = Record<string, UrlParam<unknown>>;

export type UrlValues<S extends Schema> = {
  [K in keyof S]: S[K] extends UrlParam<infer T> ? T : never;
};

export interface UrlStateOptions<S extends Schema> {
  /** The page key: back to its default whenever another key of this hook changes. */
  page?: keyof S & string;
  /** The search key: debounced (see `searchText`). */
  search?: keyof S & string;
  /** Params owned elsewhere (a child list's) that any change here removes. */
  clears?: readonly string[];
}

export interface WriteOptions {
  /** Add a history entry (opening a record). The default replaces the current one. */
  push?: boolean;
  /**
   * Remove `clears` when the patch already matches the URL. A period pick of Current period
   * writes the omitted default and still has to close the open record.
   */
  forceClears?: boolean;
}

export interface UrlState<S extends Schema> {
  /** The values in the URL. The search key's is the debounced, trimmed text. */
  values: UrlValues<S>;
  set: (patch: Partial<UrlValues<S>>, options?: WriteOptions) => void;
  /**
   * Close a panel opened under `key`. Back when this entry was pushed from the same list
   * without `key`; otherwise replace that key away.
   */
  dismiss: (key: keyof S & string) => void;
  /** The query string (`?…`, or `''`) after `patch`, for a link that opens a record. */
  searchWith: (patch: Partial<UrlValues<S>>) => string;
  /** Put on that link, so `dismiss` can tell a push from a deep link. */
  linkState: unknown;
  /** The search box's text, as typed. `''` without a `search` key. */
  searchText: string;
  setSearchText: (text: string) => void;
}

function isStateRecord(state: unknown): state is Record<string, unknown> {
  return typeof state === 'object' && state !== null;
}

function read<S extends Schema>(schema: S, params: URLSearchParams): UrlValues<S> {
  const values: Record<string, unknown> = {};
  for (const [key, param] of Object.entries(schema)) {
    const raw = params.get(key);
    const parsed = raw === null ? undefined : param.parse(raw);
    values[key] = parsed === undefined ? param.defaultValue : parsed;
  }
  return values as UrlValues<S>;
}

function apply<S extends Schema>(
  schema: S,
  options: UrlStateOptions<S>,
  params: URLSearchParams,
  patch: Partial<UrlValues<S>>,
  forceClears = false,
): URLSearchParams {
  const current: Record<string, unknown> = read(schema, params);
  const next = new URLSearchParams(params);
  let changed = false;
  for (const [key, value] of Object.entries(patch)) {
    const param = schema[key];
    if (param === undefined || value === undefined) continue;
    if (key !== options.page && !Object.is(value, current[key])) changed = true;
    if (Object.is(value, param.defaultValue)) next.delete(key);
    else next.set(key, param.serialize(value));
  }
  if (changed && options.page !== undefined && !(options.page in patch)) next.delete(options.page);
  if (changed || forceClears) {
    for (const key of options.clears ?? []) next.delete(key);
  }
  return next;
}

function asSearch(params: URLSearchParams): string {
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function useUrlState<S extends Schema>(
  schema: S,
  options: UrlStateOptions<S> = {},
): UrlState<S> {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const values = read(schema, searchParams);

  // The latest render's URL, state and options, for a write made later (the debounce's timer).
  const latest = useRef({
    searchParams,
    setSearchParams,
    state: location.state as unknown,
    search: location.search,
    options,
  });
  latest.current = {
    searchParams,
    setSearchParams,
    state: location.state as unknown,
    search: location.search,
    options,
  };
  const pendingSearch = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelPendingSearch = useCallback(() => {
    if (pendingSearch.current === undefined) return;
    clearTimeout(pendingSearch.current);
    pendingSearch.current = undefined;
  }, []);

  const set = useCallback(
    (patch: Partial<UrlValues<S>>, write: WriteOptions = {}) => {
      const { searchParams: params, setSearchParams: setParams, state, search, options: opts } =
        latest.current;
      const next = apply(schema, opts, params, patch, write.forceClears === true);
      if (next.toString() === params.toString()) return;
      if (write.push === true) cancelPendingSearch();
      // `setSearchParams` navigates, which drops the location state: keep it (e.g. `listSearch`).
      // A push remembers this entry so Close can go back to it.
      const nextState =
        write.push === true
          ? { ...(isStateRecord(state) ? state : {}), panelFrom: search }
          : state;
      setParams(next, { replace: write.push !== true, state: nextState });
    },
    [cancelPendingSearch, schema],
  );

  const dismiss = useCallback(
    (key: keyof S & string) => {
      const { searchParams: params, setSearchParams: setParams, state, options: opts } =
        latest.current;
      const param = schema[key];
      if (param === undefined) return;
      const next = apply(schema, opts, params, { [key]: param.defaultValue } as Partial<UrlValues<S>>);
      const panelFrom = isStateRecord(state) ? state.panelFrom : undefined;
      if (typeof panelFrom === 'string' && panelFrom === asSearch(next)) {
        navigate(-1);
        return;
      }
      if (next.toString() === params.toString()) return;
      setParams(next, { replace: true, state });
    },
    [navigate, schema],
  );

  // The search box: the typed text, until the URL moves to something else (Back, a link, a reset).
  const searchKey = options.search;
  const urlSearch = searchKey === undefined ? '' : String(values[searchKey]);
  const [draft, setDraft] = useState(urlSearch);
  const [seen, setSeen] = useState(urlSearch);
  if (seen !== urlSearch) {
    setSeen(urlSearch);
    if (draft.trim() !== urlSearch) setDraft(urlSearch);
  }

  const searchWith = (patch: Partial<UrlValues<S>>) => {
    let params = searchParams;
    if (searchKey !== undefined) {
      const trimmed = draft.trim();
      if (trimmed !== urlSearch) {
        params = apply(schema, options, params, { [searchKey]: trimmed } as Partial<UrlValues<S>>);
      }
    }
    return asSearch(apply(schema, options, params, patch));
  };

  const linkState = {
    ...(isStateRecord(location.state) ? location.state : {}),
    panelFrom: location.search,
  };

  useEffect(() => {
    if (searchKey === undefined) return undefined;
    const trimmed = draft.trim();
    if (trimmed === urlSearch) return undefined;
    const id = setTimeout(() => {
      set({ [searchKey]: trimmed } as Partial<UrlValues<S>>);
    }, SEARCH_DEBOUNCE_MS);
    pendingSearch.current = id;
    return () => {
      clearTimeout(id);
      if (pendingSearch.current === id) pendingSearch.current = undefined;
    };
  }, [draft, urlSearch, searchKey, set]);

  // A link's push does not go through `set`. Cancel before the timer can replace that entry.
  useEffect(() => {
    if (navigationType === 'PUSH') cancelPendingSearch();
  }, [navigationType, location.key, cancelPendingSearch]);

  return {
    values,
    set,
    dismiss,
    searchWith,
    linkState,
    searchText: draft,
    setSearchText: setDraft,
  };
}
